/**
 * Shared controls config hook. All instances stay in sync via a module listener bus.
 */

import { useCallback, useEffect, useState } from "react";

import {
  cloneControls,
  defaultControls,
  isControlsTypingTarget,
  loadControls,
  saveControls,
  shortcutConflict,
  shortcutFromEvent,
  type ControlDensity,
  type ControlId,
  type ControlsConfig,
} from "./controls";

type Listener = (config: ControlsConfig) => void;

let current = loadControls();
const listeners = new Set<Listener>();

function emit(next: ControlsConfig): void {
  current = next;
  saveControls(next);
  for (const listener of listeners) listener(next);
}

function patchControls(partial: Partial<ControlsConfig>): ControlsConfig {
  const next: ControlsConfig = {
    ...current,
    ...partial,
    labels: partial.labels ? { ...current.labels, ...partial.labels } : { ...current.labels },
    shortcuts: partial.shortcuts
      ? { ...current.shortcuts, ...partial.shortcuts }
      : { ...current.shortcuts },
    visible: partial.visible ? { ...current.visible, ...partial.visible } : { ...current.visible },
  };
  emit(next);
  return next;
}

export interface UseControlsResult {
  config: ControlsConfig;
  showBadges: boolean;
  recording: ControlId | null;
  conflict: ControlId | null;
  setKeyboardEnabled: (enabled: boolean) => void;
  setShowShortcutBadges: (show: boolean) => void;
  setDensity: (density: ControlDensity) => void;
  setLabel: (id: ControlId, label: string) => void;
  setVisible: (id: ControlId, visible: boolean) => void;
  setShortcut: (id: ControlId, shortcut: string) => ControlId | null;
  startRecording: (id: ControlId) => void;
  cancelRecording: () => void;
  resetControls: () => void;
}

/**
 * Read/write operator controls. Mutations persist to localStorage immediately.
 */
export function useControls(): UseControlsResult {
  const [config, setConfig] = useState<ControlsConfig>(() => cloneControls(current));
  const [recording, setRecording] = useState<ControlId | null>(null);
  const [conflict, setConflict] = useState<ControlId | null>(null);

  useEffect(() => {
    const listener: Listener = (next) => setConfig(cloneControls(next));
    listeners.add(listener);
    setConfig(cloneControls(current));
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (!recording) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setRecording(null);
        setConflict(null);
        return;
      }
      if (event.key === "Backspace" || event.key === "Delete") {
        if (isControlsTypingTarget(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        patchControls({ shortcuts: { ...current.shortcuts, [recording]: "" } });
        setRecording(null);
        setConflict(null);
        return;
      }
      if (isControlsTypingTarget(event.target)) return;
      const combo = shortcutFromEvent(event);
      if (!combo) return;
      event.preventDefault();
      event.stopPropagation();
      const taken = shortcutConflict(current.shortcuts, combo, recording);
      if (taken) {
        setConflict(taken);
        return;
      }
      patchControls({ shortcuts: { ...current.shortcuts, [recording]: combo } });
      setRecording(null);
      setConflict(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [recording]);

  const setKeyboardEnabled = useCallback((enabled: boolean) => {
    patchControls({ keyboardEnabled: enabled });
  }, []);

  const setShowShortcutBadges = useCallback((show: boolean) => {
    patchControls({ showShortcutBadges: show });
  }, []);

  const setDensity = useCallback((density: ControlDensity) => {
    patchControls({ density });
  }, []);

  const setLabel = useCallback((id: ControlId, label: string) => {
    patchControls({ labels: { ...current.labels, [id]: label } });
  }, []);

  const setVisible = useCallback((id: ControlId, visible: boolean) => {
    patchControls({ visible: { ...current.visible, [id]: visible } });
  }, []);

  const setShortcut = useCallback((id: ControlId, shortcut: string): ControlId | null => {
    const taken = shortcutConflict(current.shortcuts, shortcut, id);
    if (taken) {
      setConflict(taken);
      return taken;
    }
    patchControls({ shortcuts: { ...current.shortcuts, [id]: shortcut } });
    setConflict(null);
    return null;
  }, []);

  const startRecording = useCallback((id: ControlId) => {
    setConflict(null);
    setRecording(id);
  }, []);

  const cancelRecording = useCallback(() => {
    setRecording(null);
    setConflict(null);
  }, []);

  const resetControls = useCallback(() => {
    setRecording(null);
    setConflict(null);
    emit(defaultControls());
  }, []);

  return {
    config,
    showBadges: config.keyboardEnabled && config.showShortcutBadges,
    recording,
    conflict,
    setKeyboardEnabled,
    setShowShortcutBadges,
    setDensity,
    setLabel,
    setVisible,
    setShortcut,
    startRecording,
    cancelRecording,
    resetControls,
  };
}
